// Messages Auto Draft — "Recent activity" card on the Auto Draft settings page.
// Shows what the drafting job did with the last ~20 member messages so a
// missing draft is explained (rule handled it, host replied, only "thanks", …)
// instead of looking like a bug. Server contract:
// GET /api/app/messages/auto-draft/activity (server/routes/auto-draft.ts).

import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { History, RotateCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { parseErrorMessage } from "@/lib/queryClient";
import type {
  AutoDraftActivityItem,
  AutoDraftActivityOutcome,
  AutoDraftActivityResponse,
} from "@shared/auto-draft-activity";

export const AUTO_DRAFT_ACTIVITY_KEY = ["/api/app/messages/auto-draft/activity"] as const;

const OUTCOME_BADGE: Record<AutoDraftActivityOutcome, { label: string; className: string }> = {
  drafted: { label: "Drafted", className: "border-transparent bg-brand/10 text-brand" },
  sending: { label: "Sending", className: "border-transparent bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" },
  sent: { label: "Sent", className: "border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  cancelled: { label: "Cancelled", className: "border-transparent bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  failed: { label: "Failed to send", className: "border-transparent bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200" },
  skipped: { label: "Skipped", className: "border-transparent bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200" },
  working: { label: "Writing…", className: "border-transparent bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
};

function relative(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : formatDistanceToNow(d, { addSuffix: true });
}

function detailLine(item: AutoDraftActivityItem): string {
  switch (item.outcome) {
    case "drafted":
      return item.sendAt ? `Sends ${relative(item.sendAt)} unless someone edits or cancels it` : "Waiting for someone to send it";
    case "sending":
      return "Being delivered to the member";
    case "sent":
      return "The draft was sent to the member";
    case "cancelled":
      // Older rows (before cancel reasons were recorded) keep the general line.
      return item.reasonLabel
        ? `Cancelled before sending — ${item.reasonLabel}`
        : "Cancelled before sending — someone replied, a rule replied, or the conversation changed";
    case "failed":
      return "The draft was approved to send, but delivery to the member kept failing — open the conversation to retry or reply yourself";
    case "working":
      return "A draft is being written";
    case "skipped":
      return item.reasonLabel ?? "No draft was made";
  }
}

export function AutoDraftActivityCard() {
  const query = useQuery<AutoDraftActivityResponse>({
    queryKey: AUTO_DRAFT_ACTIVITY_KEY,
    refetchInterval: 60_000,
  });
  const items = query.data?.items ?? [];

  return (
    <Card className="mb-4 p-6" data-testid="card-auto-draft-activity">
      <div className="mb-1 flex items-center gap-2">
        <History className="h-4 w-4 text-brand" />
        <h2 className="text-sm font-semibold text-gray-900">Recent activity</h2>
      </div>
      <p className="mb-4 text-sm text-gray-600">
        What Auto Draft did with the latest member messages, and why a message didn’t get a draft.
      </p>

      {query.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : query.isError ? (
        <p className="text-sm text-red-600" data-testid="text-auto-draft-activity-error">
          {parseErrorMessage(query.error, "Couldn't load recent activity.")}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="text-auto-draft-activity-empty">
          Nothing yet. New member messages will show up here a few minutes after they arrive.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100" data-testid="list-auto-draft-activity">
          {items.map((item) => {
            const badge = OUTCOME_BADGE[item.outcome];
            return (
              <li key={item.id} className="flex items-start justify-between gap-3 py-3" data-testid={`row-auto-draft-activity-${item.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <Link
                      href={`/app/messages?member=${item.memberId}`}
                      className="truncate text-sm font-medium text-gray-900 hover:underline"
                      data-testid={`link-auto-draft-activity-member-${item.id}`}
                    >
                      {item.memberName || "Member"}
                    </Link>
                    <span className="text-xs text-gray-500">{relative(item.messageAt ?? item.at)}</span>
                  </div>
                  {item.messagePreview && (
                    <p className="truncate text-sm text-gray-600" title={item.messagePreview}>
                      “{item.messagePreview}”
                    </p>
                  )}
                  <p
                    className={`mt-0.5 flex items-center gap-1 text-xs ${item.outcome === "skipped" ? "text-amber-800 dark:text-amber-300" : item.outcome === "failed" ? "text-red-700 dark:text-red-300" : "text-gray-500"}`}
                    data-testid={`text-auto-draft-activity-detail-${item.id}`}
                  >
                    {detailLine(item)}
                    {item.willRetry && (
                      <span className="inline-flex items-center gap-0.5 text-gray-500">
                        <RotateCw className="h-3 w-3" /> will retry
                      </span>
                    )}
                  </p>
                </div>
                <Badge className={`shrink-0 ${badge.className}`} data-testid={`badge-auto-draft-activity-${item.id}`}>
                  {badge.label}
                </Badge>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
